import { FormField, Input, SegmentedControl, Select, Textarea } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formErrors } from '../../../lib/errors';
import type { Customer, CustomerKind, Salutation, SaveCustomerRequest } from '../api';
import styles from './crm.module.css';

const SALUTATIONS: Salutation[] = ['none', 'ms', 'mr'];

/** State of the customer form (CUS-001), shared by the dialog and quick creation. */
export function useCustomerForm(customer?: Customer) {
  const [kind, setKind] = useState<CustomerKind>(customer?.kind ?? 'person');
  const [salutation, setSalutation] = useState<Salutation>(customer?.salutation ?? 'none');
  const [firstName, setFirstName] = useState(customer?.first_name ?? '');
  const [lastName, setLastName] = useState(customer?.last_name ?? '');
  const [companyName, setCompanyName] = useState(customer?.company_name ?? '');
  const [street, setStreet] = useState(customer?.address?.street ?? '');
  const [postalCode, setPostalCode] = useState(customer?.address?.postal_code ?? '');
  const [city, setCity] = useState(customer?.address?.city ?? '');
  const [country, setCountry] = useState(customer?.address?.country ?? 'DE');
  const [email, setEmail] = useState(customer?.email ?? '');
  const [phone, setPhone] = useState(customer?.phone ?? '');
  const [notes, setNotes] = useState(customer?.notes ?? '');
  const body = (): SaveCustomerRequest => ({
    kind,
    salutation,
    first_name: firstName || null,
    last_name: lastName || null,
    company_name: kind === 'company' ? companyName || null : null,
    street: street || null,
    postal_code: postalCode || null,
    city: city || null,
    country: country || null,
    email: email || null,
    phone: phone || null,
    notes: notes || null,
  });
  return {
    kind,
    setKind,
    salutation,
    setSalutation,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    companyName,
    setCompanyName,
    street,
    setStreet,
    postalCode,
    setPostalCode,
    city,
    setCity,
    country,
    setCountry,
    email,
    setEmail,
    phone,
    setPhone,
    notes,
    setNotes,
    body,
  };
}

export type CustomerForm = ReturnType<typeof useCustomerForm>;

/** Maps API errors of a save to the fields of the customer form. */
export function customerErrors(error: unknown) {
  return formErrors(error, {
    firstName: ['customer.first_name'],
    lastName: ['customer.last_name'],
    companyName: ['customer.company_name'],
    email: ['email.'],
    phone: ['phone.'],
    street: ['address.street'],
    postalCode: ['address.postal_code'],
    city: ['address.city'],
    country: ['address.country'],
  });
}

export interface CustomerFieldsProps {
  form: CustomerForm;
  errors: ReturnType<typeof customerErrors>;
  /** Only what a quick creation needs: no phone and no notes. */
  compact?: boolean;
}

/** The fields of the customer form: kind, names, address, contact. */
export function CustomerFields({ form, errors, compact = false }: CustomerFieldsProps) {
  const { t } = useTranslation();
  const f = form;
  // The person, or the optional contact person of a company: once named,
  // both names are required (#16).
  const required = f.kind === 'person' || f.firstName.trim() !== '' || f.lastName.trim() !== '';
  const personFields = (
    <>
      <FormField label={t('crm.form.salutation')}>
        {(p) => (
          <Select
            {...p}
            value={f.salutation}
            onChange={(e) => f.setSalutation(e.target.value as Salutation)}
            options={SALUTATIONS.map((s) => ({ value: s, label: t(`crm.salutation.${s}`) }))}
          />
        )}
      </FormField>
      <div className={styles.row}>
        <FormField
          label={t('crm.form.firstName')}
          required={required}
          error={errors.field.firstName}
        >
          {(p) => (
            <Input
              {...p}
              value={f.firstName}
              maxLength={60}
              autoComplete="given-name"
              onChange={(e) => f.setFirstName(e.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('crm.form.lastName')} required={required} error={errors.field.lastName}>
          {(p) => (
            <Input
              {...p}
              value={f.lastName}
              maxLength={60}
              autoComplete="family-name"
              onChange={(e) => f.setLastName(e.target.value)}
            />
          )}
        </FormField>
      </div>
    </>
  );

  return (
    <>
      <SegmentedControl
        label={t('crm.form.kind')}
        value={f.kind}
        onChange={f.setKind}
        fullWidth
        options={[
          { value: 'person', label: t('crm.kind.person') },
          { value: 'company', label: t('crm.kind.company') },
        ]}
      />
      {f.kind === 'company' && (
        <FormField label={t('crm.form.companyName')} required error={errors.field.companyName}>
          {(p) => (
            <Input
              {...p}
              value={f.companyName}
              maxLength={120}
              autoComplete="organization"
              onChange={(e) => f.setCompanyName(e.target.value)}
            />
          )}
        </FormField>
      )}
      {f.kind === 'company' ? (
        <fieldset className={styles.section}>
          <legend>{t('crm.form.contactPerson')}</legend>
          {personFields}
        </fieldset>
      ) : (
        personFields
      )}
      <fieldset className={styles.section}>
        <legend>{t('crm.form.address')}</legend>
        <FormField label={t('crm.form.street')} error={errors.field.street}>
          {(p) => (
            <Input
              {...p}
              value={f.street}
              autoComplete="street-address"
              onChange={(e) => f.setStreet(e.target.value)}
            />
          )}
        </FormField>
        <div className={styles.addressRow}>
          <FormField label={t('crm.form.postalCode')} error={errors.field.postalCode}>
            {(p) => (
              <Input
                {...p}
                value={f.postalCode}
                autoComplete="postal-code"
                onChange={(e) => f.setPostalCode(e.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('crm.form.city')} error={errors.field.city}>
            {(p) => <Input {...p} value={f.city} onChange={(e) => f.setCity(e.target.value)} />}
          </FormField>
          <FormField label={t('crm.form.country')} error={errors.field.country}>
            {(p) => (
              <Input
                {...p}
                value={f.country}
                maxLength={2}
                onChange={(e) => f.setCountry(e.target.value.toUpperCase())}
              />
            )}
          </FormField>
        </div>
      </fieldset>
      <div className={styles.row}>
        <FormField label={t('crm.form.email')} error={errors.field.email}>
          {(p) => (
            <Input
              {...p}
              type="email"
              value={f.email}
              autoComplete="email"
              onChange={(e) => f.setEmail(e.target.value)}
            />
          )}
        </FormField>
        {!compact && (
          <FormField label={t('crm.form.phone')} error={errors.field.phone}>
            {(p) => (
              <Input
                {...p}
                type="tel"
                value={f.phone}
                onChange={(e) => f.setPhone(e.target.value)}
              />
            )}
          </FormField>
        )}
      </div>
      {!compact && (
        <FormField label={t('crm.form.notes')}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={f.notes}
              onChange={(e) => f.setNotes(e.target.value)}
            />
          )}
        </FormField>
      )}
    </>
  );
}
